import { z } from 'zod';
/** Catálogo con código canónico (inglés) y etiqueta para UI (es-CO). */
function catalog(entries) {
    const codes = entries.map((e) => e.code);
    const labels = Object.fromEntries(entries.map((e) => [e.code, e.label]));
    return { entries, codes, labels, schema: z.enum(codes) };
}
/** Tipos de llave Bre-B. */
export const BREB_ALIAS_TYPES = catalog([
    { code: 'PHONE', label: 'Celular', description: 'Número de celular (+57…)' },
    { code: 'DOCUMENT', label: 'Número de documento' },
    { code: 'EMAIL', label: 'Correo electrónico' },
    {
        code: 'ALPHANUMERIC',
        label: 'Código alfanumérico',
        description: 'Llave alfanumérica definida por el titular (@…)',
    },
    { code: 'MERCHANT_CODE', label: 'Código de comercio' },
]);
export const BreBAliasTypeSchema = BREB_ALIAS_TYPES.schema.meta({ id: 'BreBAliasType' });
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/**
 * Bre-B: la llave es un valor **único** en el directorio (DICE); el cliente solo la escribe, sin
 * elegir el tipo. De la forma del valor salen los tipos posibles, en el orden en que se consultan:
 * `@…` alfanumérica; correo; celular de 10 dígitos que empieza en 3 (o +57…); solo dígitos →
 * documento y luego código de comercio; cualquier otro texto → alfanumérica (se le antepone `@`) y
 * código de comercio. Lista vacía = no parece una llave.
 */
export function breBAliasCandidates(raw) {
    const value = raw.trim();
    if (value === '')
        return [];
    if (value.startsWith('@')) {
        return /^@[\w.-]{2,}$/.test(value)
            ? [{ aliasType: 'ALPHANUMERIC', aliasValue: value.toLowerCase() }]
            : [];
    }
    if (value.includes('@'))
        return EMAIL_RE.test(value) ? [{ aliasType: 'EMAIL', aliasValue: value.toLowerCase() }] : [];
    const compact = value.replace(/[\s.()-]/g, '');
    if (/^\+?\d+$/.test(compact)) {
        const digits = compact.replace(/^\+/, '');
        const phone = /^(?:57)?(3\d{9})$/.exec(digits)?.[1];
        const numeric = [
            { aliasType: 'DOCUMENT', aliasValue: digits },
            { aliasType: 'MERCHANT_CODE', aliasValue: digits },
        ];
        return phone ? [{ aliasType: 'PHONE', aliasValue: phone }, ...numeric] : numeric;
    }
    if (/^[\w.-]{2,}$/.test(compact)) {
        return [
            { aliasType: 'ALPHANUMERIC', aliasValue: `@${compact.toLowerCase()}` },
            { aliasType: 'MERCHANT_CODE', aliasValue: compact },
        ];
    }
    return [];
}
/** Motivos de disputa (reclamo del cliente sobre una compra). */
export const DISPUTE_REASONS = catalog([
    { code: 'NOT_RECEIVED', label: 'No recibí el producto o servicio' },
    { code: 'DUPLICATE', label: 'Me cobraron dos veces' },
    { code: 'WRONG_AMOUNT', label: 'El monto no es correcto' },
    { code: 'AGREED_REFUND', label: 'El comercio aceptó devolverme el dinero' },
    { code: 'FRAUD', label: 'Yo no hice esta compra' },
    { code: 'OTHER', label: 'Otro motivo' },
]);
export const DisputeReasonSchema = DISPUTE_REASONS.schema.meta({ id: 'DisputeReason' });
/** Tipos de evidencia / soporte documental de originación. */
export const EVIDENCE_TYPES = catalog([
    { code: 'ID_FRONT', label: 'Cédula (frente)' },
    { code: 'ID_BACK', label: 'Cédula (respaldo)' },
    { code: 'SALES_NOTEBOOK', label: 'Cuaderno de ventas' },
    { code: 'SUPPLIER_INVOICE', label: 'Factura de proveedor' },
    { code: 'BANK_STATEMENT', label: 'Extracto bancario' },
    { code: 'WALLET_STATEMENT', label: 'Extracto de billetera o datáfono/QR' },
    { code: 'BUSINESS_RECORDS', label: 'Registro de ingresos y gastos' },
    { code: 'UTILITY_BILL', label: 'Recibo de servicios públicos' },
    { code: 'RUT', label: 'RUT' },
    { code: 'CHAMBER_CERT', label: 'Certificado de Cámara de Comercio' },
    { code: 'LEASE', label: 'Contrato de arrendamiento' },
    { code: 'BUSINESS_PHOTO', label: 'Foto del negocio' },
    { code: 'INVENTORY_PHOTO', label: 'Foto del inventario' },
    { code: 'COMMERCIAL_REFERENCE', label: 'Referencia comercial' },
    { code: 'OTHER', label: 'Otro documento' },
]);
export const EvidenceTypeSchema = EVIDENCE_TYPES.schema.meta({ id: 'EvidenceType' });
/** Soportes que cuentan como "soporte de ventas" para la política de originación. */
export const SALES_EVIDENCE_TYPES = [
    'SALES_NOTEBOOK',
    'SUPPLIER_INVOICE',
    'BANK_STATEMENT',
    'WALLET_STATEMENT',
    'BUSINESS_RECORDS',
];
/**
 * Qué debe tener cada soporte para que sirva (texto para el cliente, es-CO). Lo usan la tarjeta para
 * subirlo, el mensaje cuando no sirve y la guía del asesor. Vigencias alineadas con el lector
 * (`MAX_AGE_DAYS` del dominio de originación).
 */
export const EVIDENCE_REQUIREMENTS = {
    ID_FRONT: ['Toda la cédula a la vista, sin reflejos, con la foto y el número legibles.'],
    ID_BACK: ['Todo el respaldo a la vista, con la huella y el código legibles.'],
    BANK_STATEMENT: [
        'Descárgalo en PDF desde la app o la página de tu banco (no una foto de la pantalla editada).',
        'A tu nombre y de los últimos 2 meses.',
        'Que se vean los movimientos: lo que te entra por ventas y lo que pagas.',
    ],
    WALLET_STATEMENT: [
        'Extracto o historial de Nequi, Daviplata, datáfono o QR, descargado de la app.',
        'A tu nombre y de los últimos 2 meses, con los pagos que recibes de tus clientes.',
    ],
    SUPPLIER_INVOICE: [
        'Facturas o remisiones de compra de mercancía o insumos de los últimos 3 meses.',
        'Que se vean el proveedor, la fecha, tu nombre o el de tu negocio y el valor total.',
    ],
    BUSINESS_RECORDS: [
        'El registro donde anotas las ventas y los gastos del negocio: un Excel, la app donde llevas las cuentas o el libro fiscal de operaciones diarias.',
        'De los últimos 2 a 3 meses, con la fecha de cada movimiento o el total de cada día o semana.',
        'Que tenga lo que entra (ventas) y lo que sale (compras de mercancía, arriendo, servicios, pagos a empleados).',
        'Sube el archivo original (Excel, CSV o el PDF que descargas de la app); si es una foto, que se vean todas las columnas y los totales.',
    ],
    SALES_NOTEBOOK: [
        'Fotos de varias semanas recientes (máximo 2 meses), con las fechas y el total de cada día.',
        'Sirve como complemento: acompáñalo de un extracto o de facturas de tus proveedores.',
    ],
    UTILITY_BILL: [
        'Recibo de luz, agua o gas de la casa o del negocio, de los últimos 3 meses.',
        'Completo, con la dirección y la fecha visibles.',
    ],
    RUT: ['El RUT completo, descargado de la DIAN, con tu nombre y la actividad del negocio.'],
    CHAMBER_CERT: ['Certificado de Cámara de Comercio de los últimos 3 meses.'],
    LEASE: ['Contrato completo y firmado, con la dirección del local y el valor del arriendo.'],
    BUSINESS_PHOTO: ['Foto de frente del negocio, con el aviso o la entrada a la vista.'],
    INVENTORY_PHOTO: ['Foto de la mercancía o los insumos que tienes para vender.'],
    COMMERCIAL_REFERENCE: [
        'Carta o mensaje de un proveedor o cliente frecuente con su nombre, teléfono y desde cuándo te conoce.',
    ],
    OTHER: ['Un documento que muestre las ventas, compras o ingresos de tu negocio.'],
};
/** Tipos de documento de identidad colombianos. */
export const DOCUMENT_TYPES = catalog([
    { code: 'CC', label: 'Cédula de ciudadanía' },
    { code: 'CE', label: 'Cédula de extranjería' },
    { code: 'PPT', label: 'Permiso por protección temporal' },
    { code: 'PA', label: 'Pasaporte' },
    { code: 'NIT', label: 'NIT' },
    { code: 'TI', label: 'Tarjeta de identidad' },
]);
export const DocumentTypeSchema = DOCUMENT_TYPES.schema.meta({ id: 'DocumentType' });
//# sourceMappingURL=domain-catalogs.js.map